import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  Plus,
  FileDown,
  CheckCircle2,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { episService } from '../../services/epis.service';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { EntregaEpiModal } from '../../pages/Epis/components/EntregaEpiModal';
import { DetalhesEntregaModal } from '../../pages/Epis/components/DetalhesEntregaModal';
import type {
  EpiEntregaContract,
  EpiCronogramaVigenciaItemContract,
} from '@sstexames/shared-types';

interface ColaboradorEpisTabProps {
  colaboradorId: number;
}

export default function ColaboradorEpisTab({ colaboradorId }: ColaboradorEpisTabProps) {
  const { empresa } = useEmpresa();
  const [loading, setLoading] = useState(false);
  const [entregas, setEntregas] = useState<EpiEntregaContract[]>([]);
  const [vigencias, setVigencias] = useState<EpiCronogramaVigenciaItemContract[]>([]);
  const [isEntregaModalOpen, setIsEntregaModalOpen] = useState(false);
  const [selectedEntrega, setSelectedEntrega] = useState<EpiEntregaContract | null>(null);
  const [isDetalhesModalOpen, setIsDetalhesModalOpen] = useState(false);

  const loadDados = useCallback(async () => {
    if (!empresa?.id || !colaboradorId) return;
    try {
      setLoading(true);
      const [entregasRes, vigenciasRes] = await Promise.all([
        episService.getEntregas(empresa.id, { colaboradorId }),
        episService.getCronogramaVigencias(empresa.id),
      ]);

      setEntregas(entregasRes || []);
      // Filtrar apenas vigências deste colaborador
      setVigencias((vigenciasRes || []).filter((v) => v.colaborador_id === colaboradorId));
    } catch (err) {
      console.error('Erro ao carregar dados de EPIs do colaborador:', err);
    } finally {
      setLoading(false);
    }
  }, [empresa?.id, colaboradorId]);

  useEffect(() => {
    loadDados();
  }, [loadDados]);

  const handleDownloadPdf = async (entrega: EpiEntregaContract) => {
    try {
      await episService.downloadPdfFile(
        entrega.id,
        `ficha_epi_${entrega.id}_${entrega.colaborador_nome.replace(/\s+/g, '_')}.pdf`,
        entrega.empresa_id,
      );
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header com Ações */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary-600" /> Equipamentos de Proteção Individual (EPIs)
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Histórico de fornecimento, fichas assinadas digitalmente e controle de validade dos equipamentos em posse.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEntregaModalOpen(true)}
            className="px-3.5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-colors"
          >
            <Plus className="w-4 h-4" /> Nova Entrega de EPI
          </button>
          <button
            onClick={loadDados}
            disabled={loading}
            className="p-2 border border-gray-300 bg-white rounded-xl hover:bg-gray-50 text-gray-600 shadow-2xs transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-xs text-gray-400">
          Carregando informações de EPIs...
        </div>
      ) : (
        <>
          {/* Equipamentos em Posse */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                EPIs em Posse / Cronograma de Troca ({vigencias.length})
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2">Equipamento</th>
                    <th className="pb-2">CA</th>
                    <th className="pb-2">Entregue em</th>
                    <th className="pb-2">Previsão de Troca</th>
                    <th className="pb-2 text-center">Dias Restantes</th>
                    <th className="pb-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vigencias.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-gray-400">
                        Nenhum equipamento ativo registrado em posse do colaborador.
                      </td>
                    </tr>
                  ) : (
                    vigencias.map((v) => {
                      const diasRestantes = v.dias_restantes ?? v.dias_para_troca ?? 0;
                      return (
                        <tr key={v.entrega_item_id} className="hover:bg-gray-50/60">
                          <td className="py-2.5 font-bold text-gray-900">{v.descricao_epi || v.epi_descricao}</td>
                          <td className="py-2.5 font-mono text-gray-600">{v.ca_numero || 'Certificado'}</td>
                          <td className="py-2.5 text-gray-600">
                            {new Date(v.data_entrega).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="py-2.5 text-gray-800 font-medium">
                            {new Date(v.data_prevista_troca).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="py-2.5 text-center font-bold">
                            {diasRestantes < 0 ? (
                              <span className="text-rose-600">Vencido ({Math.abs(diasRestantes)}d atrás)</span>
                            ) : (
                              <span className="text-gray-800">{diasRestantes} dias</span>
                            )}
                          </td>
                          <td className="py-2.5 text-right">
                            {v.status_vigencia === 'vencido' ? (
                              <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded">
                                Vencido
                              </span>
                            ) : (v.status_vigencia as string) === 'a_vencer' || v.status_vigencia === 'alerta_30_dias' || v.status_vigencia === 'alerta_15_dias' ? (
                              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                                A Vencer
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                                Vigente
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Histórico de Fichas de Entrega */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <span className="text-xs font-bold text-gray-800 uppercase tracking-wider block">
              Histórico de Fichas de Entrega NR-06 ({entregas.length})
            </span>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2">Ficha</th>
                    <th className="pb-2">Data Emissão</th>
                    <th className="pb-2 text-center">Itens</th>
                    <th className="pb-2">Método</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entregas.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-gray-400">
                        Nenhuma ficha de entrega emitida para este colaborador.
                      </td>
                    </tr>
                  ) : (
                    entregas.map((ent) => (
                      <tr key={ent.id} className="hover:bg-gray-50/60">
                        <td className="py-2.5 font-mono font-bold text-gray-900">#{ent.id}</td>
                        <td className="py-2.5 text-gray-600">
                          {new Date(ent.data_entrega).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-2.5 text-center font-bold text-gray-800">
                          {ent.itens.length}
                        </td>
                        <td className="py-2.5 text-gray-600">
                          {ent.metodo_assinatura === 'link_celular' ? 'Link Celular' : 'Presencial'}
                        </td>
                        <td className="py-2.5">
                          {ent.status === 'entregue' ? (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Assinada
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Pendente
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => {
                                setSelectedEntrega(ent);
                                setIsDetalhesModalOpen(true);
                              }}
                              className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                            >
                              Ver Ficha
                            </button>
                            <button
                              onClick={() => handleDownloadPdf(ent)}
                              title="Baixar PDF NR-06"
                              className="p-1 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg transition-colors"
                            >
                              <FileDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modais */}
      <EntregaEpiModal
        isOpen={isEntregaModalOpen}
        onClose={() => setIsEntregaModalOpen(false)}
        onSuccess={() => {
          loadDados();
        }}
        empresaId={empresa?.id || 0}
        initialColaboradorId={colaboradorId}
      />

      <DetalhesEntregaModal
        isOpen={isDetalhesModalOpen}
        onClose={() => {
          setIsDetalhesModalOpen(false);
          setSelectedEntrega(null);
        }}
        entrega={selectedEntrega}
      />
    </div>
  );
}
